import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './cubit-table.module.css';

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import {
  CubitTableHeaderProps,
  CubitTableProps,
  CubitTableColumn,
  OrderDirection,
  Paging,
} from './cubit-table.types';
import { CubitTableHeader } from './cubit-table-header';
import TableBody from '@mui/material/TableBody';
import _ from 'lodash';
import get from 'lodash/get';
import find from 'lodash/find';
import TablePagination from '@mui/material/TablePagination';
import { useDispatch, useSelector } from 'react-redux';
import {
  actionCubitTableOrder,
  actionCubitTableSelect,
} from './cubit-table-actions';
import {
  arraySection,
  arrayDistinct,
  arrayRemove,
  arrayRemoveAtIndex,
} from '../../utils';
import { CubitCheckbox } from '../cubit-inputs/cubit-checkbox/cubit-checkbox';
import classNames from 'classnames';
import { filter } from 'lodash';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles((theme) => ({
  tablePagination: {
    paddingLeft: '6px',
    fontWeight: 'bold',
    margin: '0px',
    '& .MuiTablePagination-displayedRows': {
      margin: '0px',
    },

    '& .MuiInputBase-root': {
      display: 'none',
    },
  },
  table: {
    '& .MuiPaper-root': {
      borderRadius: '0px',
      boxShadow: 'none',
    },
  },
}));
const DEFAULT_ROWS_PER_PAGE = [20, 25, 30];

const isPagingObject = (paging: boolean | Paging): paging is Paging => {
  return (
    typeof paging === 'object' && paging !== null && 'rowsPerPage' in paging
  );
};

// Only ISO 8601 strings count as dates. new Date() reads plain text such as
// "Totakvegen 796" as the year 796, so it must never see arbitrary values.
const ISO_DATE_REGEX =
  /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

const isIsoDateString = (value: any): value is string =>
  typeof value === 'string' && ISO_DATE_REGEX.test(value);

// A date-only ISO string is parsed as UTC by new Date(); read it as local midnight instead
const parseIsoDate = (value: string): Date =>
  new Date(value.length === 10 ? `${value}T00:00:00` : value);

const formatDate = (value: any, language: string): string => {
  if (!value || value === '-') return String(value);

  const isDateObject = value instanceof Date;

  if (!isIsoDateString(value) && !isDateObject) return String(value);

  const date = isDateObject ? value : parseIsoDate(value);
  if (isNaN(date.getTime())) return String(value);

  if (language === 'nb') {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  } else {
    return date.toLocaleString();
  }
};

const CubitTable: React.FC<CubitTableProps> = (props) => {
  const {
    columns,
    data,
    selectable,
    sorting,
    paging,
    name: tableName,
    onRowClick,
    flat,
    style,
  } = props;

  const tableStyles = useStyles();
  const { i18n } = useTranslation();

  let initialSortingDirection: 'asc' | 'desc' = 'asc';

  const firstSortableColumn = find(
    columns,
    (column) => column.sortable !== false
  );
  let initialSortingColumn = firstSortableColumn
    ? firstSortableColumn.key
    : columns[0].key;

  if (sorting && typeof sorting === 'object') {
    initialSortingDirection = sorting.direction;
    initialSortingColumn = sorting.by;
  }

  const dispatch = useDispatch();
  const onOrderChange = (
    tableName: string,
    orderBy: string,
    orderDirection: OrderDirection
  ) => dispatch(actionCubitTableOrder(tableName, orderBy, orderDirection));

  const order =
    useSelector(
      (state: any) =>
        state.table[tableName] && state.table[tableName].orderDirection
    ) || initialSortingDirection;

  const orderBy =
    useSelector(
      (state: any) => state.table[tableName] && state.table[tableName].orderBy
    ) || initialSortingColumn;

  const initialRowsPerPage = isPagingObject(paging)
    ? paging.rowsPerPage[0]
    : DEFAULT_ROWS_PER_PAGE[0];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const selected = useSelector((state: any) =>
    state.table[tableName] && state.table[tableName].selection
      ? state.table[tableName].selection
      : []
  );

  const setSelected = (tableName: string, selection: any[]) =>
    dispatch(actionCubitTableSelect(tableName, selection));
  const [previouslyToggledCheckboxIndex, setPreviouslyToggledCheckboxIndex] =
    useState(0);

  let tableHeaderProps: CubitTableHeaderProps = {
    columns: columns,
  };

  let transformedData = data;
  const rowClickHandler = (
    row: any,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (typeof onRowClick === 'function') {
      onRowClick(row, event);
    }
  };

  let renderRowCheckbox: (
    index: number,
    id: string,
    selected: boolean
  ) => JSX.Element | null = (index, id, selected) => null;

  if (selectable) {
    const handleSelectAllChange = (event: ChangeEvent<HTMLInputElement>) => {
      const isSelectAllCurrentlyChecked = event.currentTarget.checked;
      let selected = [];

      if (isSelectAllCurrentlyChecked) {
        if (typeof selectable === 'function') {
          selected = filter(data, selectable).map((n) => n.id);
        } else {
          selected = data.map((n) => n.id);
        }
      }

      setSelected(tableName, selected);
    };

    const handleCheckboxChange = (
      event: React.ChangeEvent<HTMLInputElement>,
      index: number,
      id: string
    ) => {
      const nativeEvent = event.nativeEvent as unknown as React.MouseEvent;
      const isShiftKeyPressed = nativeEvent.shiftKey;
      const itemAlreadySelected = selected.indexOf(id) !== -1;

      let newSelected: any[] = [];

      if (isShiftKeyPressed) {
        const selection = arraySection(
          data,
          previouslyToggledCheckboxIndex,
          index,
          true
        );
        const selectableItemsInSelection =
          typeof selectable === 'function'
            ? filter(selection, selectable)
            : selection;
        const selectableItemsIds = selectableItemsInSelection.map(
          (i: any) => i.id
        );

        if (itemAlreadySelected) {
          newSelected = arrayRemove(selected, selectableItemsIds);
        } else {
          newSelected = arrayDistinct(
            newSelected.concat(selected, selectableItemsIds)
          );
        }
      } else {
        if (itemAlreadySelected) {
          newSelected = arrayRemoveAtIndex(selected, selected.indexOf(id));
        } else {
          newSelected = newSelected.concat(selected, id);
        }
      }

      setPreviouslyToggledCheckboxIndex(index);

      setSelected(tableName, newSelected);
    };

    renderRowCheckbox = (index: number, rowData: any, selected: boolean) => {
      const isRowSelectable =
        typeof selectable === 'function' ? selectable(rowData) : selectable;

      return isRowSelectable ? (
        <td className={styles.checkboxCell}>
          <CubitCheckbox
            checked={selected}
            onChange={(event) => handleCheckboxChange(event, index, rowData.id)}
            onClick={(e) => e.stopPropagation()}
          />
        </td>
      ) : (
        <td />
      );
    };

    tableHeaderProps = {
      ...tableHeaderProps,
      select: {
        selectedCount: selected.length,
        totalSelectableCount:
          typeof selectable === 'function'
            ? filter(data, selectable).map((n) => n.id).length
            : data.length,
        onSelectAll: handleSelectAllChange,
      },
    };
  }
  if (sorting) {
    const handleSort = (
      event: React.MouseEvent<HTMLButtonElement>,
      property: string
    ) => {
      const isDesc = orderBy === property && order === 'desc';
      onOrderChange(tableName, property, isDesc ? 'asc' : 'desc');
    };

    tableHeaderProps = {
      ...tableHeaderProps,
      sorting: { direction: order, by: orderBy, onSort: handleSort },
    };
    const sortedValues = transformedData
      .map((item) => item[orderBy])
      .filter((value) => value !== '-');
    const isDateField =
      sortedValues.length > 0 && sortedValues.every(isIsoDateString);

    transformedData = _.orderBy(
      transformedData,
      [
        (item) => item[orderBy] === '-', // moving "-" values to the end
        (item) => {
          const value = item[orderBy];
          if (isDateField) {
            return parseIsoDate(value).getTime();
          }
          return value;
        },
      ],
      ['asc', order]
    );
  }
  //#endregion

  let pagingElement: JSX.Element | null = null;

  if (paging) {
    const handleChangePage = (event: any, newPage: any) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    pagingElement = (
      <TablePagination
        className={tableStyles.tablePagination}
        component='div'
        labelRowsPerPage={''}
        count={data.length}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={
          isPagingObject(paging) ? paging.rowsPerPage : DEFAULT_ROWS_PER_PAGE
        }
        page={page}
        showFirstButton
        showLastButton
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    );

    transformedData = transformedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }

  const renderTableRow = (
    index: number,
    row: any,
    columns: CubitTableColumn[]
  ) => {
    const isItemSelected = selected.indexOf(row.id) !== -1;

    return (
      <tr
        key={row.id || index}
        className={classNames(styles.tableRow, {
          [styles.tableRowSelected]: isItemSelected,
          [styles.tableRowClickable]: typeof onRowClick === 'function',
        })}
        tabIndex={-1}
        onClick={(event) => rowClickHandler(row, event)}>
        {renderRowCheckbox(index, row, isItemSelected)}

        {columns.map((column, index) => {
          const value = column.getDisplayableElement
            ? column.getDisplayableElement(row)
            : get(row, column.key);

          const formattedValue = formatDate(value, i18n.language);

          return (
            <td
              key={index}
              className={styles.tableCell}
              style={{ textAlign: column.align, width: column.width }}>
              {formattedValue}
            </td>
          );
        })}
      </tr>
    );
  };

  const renderTable = () => {
    return (
      <>
        <div style={{ width: '100%', overflow: 'auto' }}>
          <Table style={style}>
            <CubitTableHeader {...tableHeaderProps} />
            <TableBody>
              {transformedData.map((row: any, index: number) =>
                renderTableRow(index, row, columns)
              )}

              {/* {whiteSpaceElement} */}
            </TableBody>
          </Table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {pagingElement}
        </div>
      </>
    );
  };

  return flat ? (
    renderTable()
  ) : (
    <Paper
      style={{ borderRadius: '0px', boxShadow: 'none' }}
      className={tableStyles.table}>
      {renderTable()}
    </Paper>
  );
};

export default CubitTable;
